import * as Sentry from '@sentry/react';
import type { Log } from '@sentry/core';
import { normalizeMacroSentryBreadcrumb } from '@playcastdotio/Observability';
import { scrubPii, scrubAuthHeaders, NOISE_DROP_PATTERNS, isLogLevelEnabled, setCorrelationId, clearCorrelationId, normalizeMacroSentryEvent, normalizeMacroSentryLog, type CorrelatableScope } from '@playcastdotio/Observability';
import type { LogLevel } from '@playcastdotio/Observability';

const ALLOWED_BREADCRUMB_CATEGORIES = new Set([
  'fetch',
  'xhr',
  'console',
  'navigation',
  'ui.click',
]);

/**
 * @internal — exported for test only.
 * Scrubs PII and drops events matching NOISE_DROP_PATTERNS.
 */
export function beforeSend(event: Sentry.Event): Sentry.Event | null {
  scrubPii(event as Parameters<typeof scrubPii>[0]);
  normalizeMacroSentryEvent(event as unknown as Parameters<typeof normalizeMacroSentryEvent>[0]);

  // Fatal events always ship — never drop them regardless of noise patterns.
  if (event.level === 'fatal') return event;

  const message = event.message ?? event.exception?.values?.[0]?.value ?? '';
  for (const pattern of NOISE_DROP_PATTERNS) {
    if (pattern.test(message)) {
      return null;
    }
  }

  return event;
}

function makeSentryBeforeSend() {
  return (event: Sentry.ErrorEvent): Sentry.ErrorEvent | null => {
    return beforeSend(event) as Sentry.ErrorEvent | null;
  };
}

/**
 * @internal — exported for test only.
 * Drops logs below the minimum level for the given environment.
 * - production: error + fatal only
 * - preview: TEMPORARY - all levels pass (normally warn and above; see note below)
 * - development: all levels
 */
export function beforeSendLog(
  log: { level?: string },
  environment: string,
): typeof log | null {
  const level = (log.level ?? 'info') as LogLevel;

  if (environment === 'production') {
    return isLogLevelEnabled(level, 'error')
      ? normalizeMacroSentryLog(log as Parameters<typeof normalizeMacroSentryLog>[0])
      : null;
  }

  // TEMPORARY (sentry preview log testing): preview passes ALL log levels so we
  // can validate end-to-end Sentry.logger forwarding in prerelease. This bypasses
  // the OBSERVABILITY_SPEC §3 prerelease policy (warn+ only) until the §10 Phase 4
  // Adaptive Log Level System ships a runtime per-host override. REVERT then.
  // Original: return isLogLevelEnabled(level, 'warn') ? log : null;
  if (environment === 'preview') {
    return normalizeMacroSentryLog(log as Parameters<typeof normalizeMacroSentryLog>[0]);
  }

  return normalizeMacroSentryLog(log as Parameters<typeof normalizeMacroSentryLog>[0]);
}

/**
 * @internal — exported for test only.
 * Drops disallowed breadcrumb categories; scrubs auth headers from network breadcrumbs.
 */
export function beforeBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb | null {
  breadcrumb = normalizeMacroSentryBreadcrumb({ ...breadcrumb });
  const category = breadcrumb.category ?? '';

  if (!ALLOWED_BREADCRUMB_CATEGORIES.has(category)) {
    return null;
  }

  if ((category === 'fetch' || category === 'xhr') && breadcrumb.data) {
    const data = { ...breadcrumb.data };
    scrubAuthHeaders(data as Record<string, unknown>);

    if (data['request_headers'] && typeof data['request_headers'] === 'object') {
      const headers = { ...(data['request_headers'] as Record<string, string>) };
      scrubAuthHeaders(headers as Record<string, unknown>);
      data['request_headers'] = headers;
    }

    return { ...breadcrumb, data };
  }

  return breadcrumb;
}

function makeTracesSampler(environment: string) {
  return (_samplingContext: unknown): number => {
    if (environment === 'development') return 1.0;
    if (environment === 'preview') return 0.1;
    return 0.01;
  };
}

function makeSentryBeforeSendLog(environment: string) {
  return (log: Log): Log | null => {
    return beforeSendLog(log, environment) as Log | null;
  };
}

function correlatableScope(): CorrelatableScope {
  return Sentry.getCurrentScope() as unknown as CorrelatableScope;
}

/**
 * Initialize Sentry for the host-frontend renderer process.
 * Call once at the top of main.tsx, before React rendering.
 */
export function initSentryRenderer(options: {
  dsn: string;
  version: string;
  environment: string;
  branch: string;
}): void {
  const { dsn, version, environment, branch } = options;

  const isRelease = branch === 'release';

  Sentry.init({
    dsn,
    release: version,
    environment,
    normalizeDepth: 10,
    enableLogs: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/(?!.*clerk).*\.playcast\.io/,
      /^https:\/\/(?!.*clerk).*\.gibber\.com/,
      /^https:\/\/(?!.*clerk).*\.api\.gibber\.com/,
    ],
    tracesSampler: makeTracesSampler(environment),
    replaysSessionSampleRate: isRelease ? 0 : 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend: makeSentryBeforeSend(),
    beforeSendLog: makeSentryBeforeSendLog(environment),
    beforeBreadcrumb,
  });

  // Mirror `service` as a log attribute so renderer Logs are searchable by it
  // (Sentry promotes scope attributes, not tags, onto logs).
  Sentry.setTag('service', 'host-frontend');
  setCorrelationId(correlatableScope(), 'service', 'host-frontend');
}

/**
 * Set machine_id on the Sentry scope as both a tag (Errors) and a log
 * attribute (Logs). Sentry only promotes scope *attributes* (not tags) onto
 * logs, so the attribute is required for logs to be searchable by machine_id.
 * Call when machineId becomes available.
 */
export function setSentryMachineId(machineId: string): void {
  setCorrelationId(correlatableScope(), 'machine_id', machineId);
}

interface MachineIdStore {
  currentState: string;
  addListener: (callback: (id: string) => void) => string;
  removeListener: (listenerId: string) => void;
}

/**
 * Subscribe to a machineIdState store and keep the machine_id Sentry tag
 * current for the lifetime of the process.
 *
 * Sets the tag immediately if the id is already available, then re-applies it
 * on every subsequent state update. Call once at app startup before React
 * renders so the tag is present on all events.
 *
 * Returns a cleanup function that removes the listener (useful for tests).
 */
export function subscribeMachineIdToSentry(store: MachineIdStore): () => void {
  const apply = (id: string) => {
    if (id) setSentryMachineId(id);
  };

  apply(store.currentState);
  const listenerId = store.addListener(apply);

  return () => store.removeListener(listenerId);
}

/**
 * Set broadcast_id on the current Sentry scope as both a tag (Errors) and a
 * log attribute (Logs). Call when the host starts broadcasting
 * (PEER_GET_ID_ANSWER received).
 */
export function setSentryBroadcastId(broadcastId: string): void {
  setCorrelationId(correlatableScope(), 'broadcast_id', broadcastId);
}

/**
 * Clear the broadcast_id tag and log attribute on the current Sentry scope.
 * Call when the host stops broadcasting so subsequent events do not
 * carry a stale peer ID.
 */
export function clearSentryBroadcastId(): void {
  clearCorrelationId(correlatableScope(), 'broadcast_id');
}

// `hostPeerId` is optional in the HostPresence schema (zod .optional()), so
// the interface must reflect `string | undefined` to accept the real StateStore.
interface HostPresenceLike {
  currentState: { hostPeerId?: string };
  addListener: (callback: (state: { hostPeerId?: string }) => void) => string;
  removeListener: (listenerId: string) => void;
}

/**
 * Subscribe to a hostPresenceState-shaped store and keep the broadcast_id
 * Sentry tag aligned with the host's current `hostPeerId`.
 *
 * - Sets the tag when `hostPeerId` becomes non-empty (broadcast starts).
 * - Clears the tag when `hostPeerId` becomes empty or undefined (broadcast stops).
 * - No-ops on repeated emissions of the same value.
 *
 * Per docs/OBSERVABILITY_SPEC.md §6, the renderer owns wiring the
 * `broadcast_id` tag after `PEER_GET_ID_ANSWER` updates `hostPeerId`.
 * Call once at app startup before React renders.
 *
 * Returns a cleanup function that removes the listener (useful for tests).
 */
export function subscribeBroadcastIdToSentry(store: HostPresenceLike): () => void {
  let lastApplied = '';

  const apply = (peerId: string | undefined) => {
    const next = peerId ?? '';
    if (next === lastApplied) return;
    lastApplied = next;
    if (next) {
      setSentryBroadcastId(next);
    } else {
      clearSentryBroadcastId();
    }
  };

  apply(store.currentState.hostPeerId);
  const listenerId = store.addListener((state) => apply(state.hostPeerId));

  return () => store.removeListener(listenerId);
}
